/**
 * CaseMacro — case-level operator overview.
 *
 * Decision (MACRO_OVERVIEW_PLAN.md #6): we wait for an explicit user click
 * before running anything, and use that opportunity to TELL the user what's
 * about to happen — so the explainer card is the landing state.
 *
 * Stages 0–2 are bundled into a single click and a single network call to
 * POST /api/v1/operators/case/{id}/macro/run:
 *   0. Ledger summary (regime, time profile, per-chain breakdown)
 *   1. Entity attribution for every counterparty (DB-only, skip_fallback)
 *   2. Wallet clustering (auto_cluster — KYC + same-EVM + BTC co-spend)
 *
 * All three are deterministic and free.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ArrowLeft, ScanLine, Layers, Database, Zap, Clock, Coins,
    ShieldCheck, AlertTriangle, RefreshCw, Loader2, CheckCircle2,
    TrendingUp, TrendingDown, Activity, Tag, Radio, ChevronDown, ChevronUp,
    Network, Flame, ArrowUpRight, ArrowDownLeft, FileText, Brain, Lock,
} from 'lucide-react';
import { toast } from 'sonner';
import { brandGhostBtn, brandIconAccent } from '../components/brandChrome';
import {
    operatorService,
    caseService,
    entityEnrichmentService,
    type OperatorCluster,
    type OperatorClusterMember,
    type LedgerSummary,
    type EntityAttribution,
    type LabelledCounterparty,
    type MacroRunResponse,
    type OnchainContextResponse,
    type ClusterTraceResponse,
    type ConsolidatedCounterparty,
    type ClusterTraceMemberSummary,
    type MacroSynthesisResponse,
    type DetectedPattern,
} from '../services/api';
import { PageBreadcrumb } from '../components/PageBreadcrumb';

// ── Stage table for the explainer ──────────────────────────────────────

interface StageRow {
    n: number;
    label: string;
    desc: string;
    cost: 'free' | 'paid' | 'paid_per_wallet';
    eta: string;
    bundled: boolean;     // included in the "Run Stages 0–2" click
    live: boolean;        // wired in this PR
}

const STAGES: StageRow[] = [
    { n: 0, label: 'Ledger summary',     desc: 'Total in/out per chain, time profile, regime (accumulator / distributor / pass-through)', cost: 'free', eta: '~1s', bundled: true, live: true },
    { n: 1, label: 'Entity attribution', desc: '1.27M-entity DB lookup across every subpoena counterparty',                                  cost: 'free', eta: '~3s', bundled: true, live: true },
    { n: 2, label: 'Wallet clustering',  desc: 'Tie this operator\'s wallets together via KYC join + same-EVM + BTC co-spend',               cost: 'free', eta: '~5s', bundled: true, live: true },
    { n: 3, label: 'On-chain context',   desc: 'Per-wallet recent activity outside the subpoena window — pulls fresh chain data + new counterparty labels', cost: 'paid_per_wallet', eta: '5–10s/wallet', bundled: false, live: true },
    { n: 4, label: 'Cluster trace',      desc: 'Fan out Stage 3 across the top-N cluster members and consolidate the off-ramps + high-risk hits + new counterparties', cost: 'paid', eta: '10–60s', bundled: false, live: true },
    { n: 5, label: 'Pattern + AI synthesis',   desc: 'Deterministic pattern detector over Stages 0–4 + AI executive summary for the LE report', cost: 'paid', eta: '~10s', bundled: false, live: true },
];

// ── Stepper definition ────────────────────────────────────────────────
// Visual numbering 1–5 (drops Stage 3 = on-chain pull, which lives inline
// inside the Cluster step's per-member rows). Each step is a clickable tab;
// arrows between chips signal the natural left-to-right reading order.
type StepKey = 'cluster' | 'ledger' | 'entities' | 'trace' | 'synthesis';
type StepStatus = 'pending' | 'ready' | 'running' | 'done' | 'locked';

interface StepDef {
    key: StepKey;
    n: number;
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    cost: 'free' | 'paid';
}

// Cluster first — it's the "who am I looking at" anchor and the most
// data-dense view, so it makes the best landing pane after Stages 0-2 run.
const STEP_DEFS: StepDef[] = [
    { key: 'cluster',   n: 1, label: 'Cluster',   icon: Layers,   cost: 'free' },
    { key: 'ledger',    n: 2, label: 'Ledger',    icon: Activity, cost: 'free' },
    { key: 'entities',  n: 3, label: 'Entities',  icon: Tag,      cost: 'free' },
    { key: 'trace',     n: 4, label: 'Trace',     icon: Network,  cost: 'paid' },
    { key: 'synthesis', n: 5, label: 'Synthesis', icon: Brain,    cost: 'paid' },
];

// ── Top-level page ─────────────────────────────────────────────────────

export const CaseMacro: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const [caseTitle, setCaseTitle] = useState<string>('');
    const [macro, setMacro] = useState<MacroRunResponse | null>(null);
    const [running, setRunning] = useState(false);
    const [loadingExisting, setLoadingExisting] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Stage 4 state — separate so cluster-trace can run independently of the
    // bundled 0–2 macro and persist while the user explores the rest.
    const [clusterTrace, setClusterTrace] = useState<ClusterTraceResponse | null>(null);
    const [tracing, setTracing] = useState(false);
    const [traceMaxMembers, setTraceMaxMembers] = useState(10);

    // Stage 5 state — pattern detector + AI synthesis. Persisted in component
    // memory; user re-runs explicitly. We do NOT auto-run — the LLM call costs
    // money and the patterns alone are useful even without it.
    const [synthesis, setSynthesis] = useState<MacroSynthesisResponse | null>(null);
    const [synthesizing, setSynthesizing] = useState(false);

    // Active stepper tab. Defaults to Cluster (the punchline pane). Auto-jumps
    // when a paid step finishes so the user lands on the result they just paid for.
    const [activeStep, setActiveStep] = useState<StepKey>('cluster');

    // Per-wallet on-chain pulls (Stage 3). Lifted from individual MemberRow
    // state so they survive tab switches AND get fed into Stage 5 synthesis.
    // Keyed by `{chain}:{address}`. Empty until the user clicks "Pull on-chain".
    const [onchainContexts, setOnchainContexts] = useState<Record<string, OnchainContextResponse>>({});

    const recordOnchainContext = useCallback((chain: string, address: string, ctx: OnchainContextResponse) => {
        setOnchainContexts(prev => ({ ...prev, [`${chain}:${address}`]: ctx }));
    }, []);

    // Initial load — if a cluster already exists, the user has run this before.
    // We don't have a persisted ledger/entities cache in v1, so we just probe
    // for the cluster; if it exists we show a "Click Re-run to refresh" hint
    // alongside the cluster. The user re-runs to get fresh ledger + entities.
    useEffect(() => {
        if (!id) return;
        let cancelled = false;
        (async () => {
            try {
                const [c, kase] = await Promise.all([
                    operatorService.getClusterForCase(id),
                    caseService.get(id).catch(() => null),
                ]);
                if (cancelled) return;
                if (c) {
                    // Synthesise a partial macro so the user sees the cluster
                    // immediately. Ledger + entities will populate on Re-run.
                    setMacro({
                        cluster: c,
                        ledger: null as any,
                        entities: null as any,
                    });
                }
                if (kase?.title) setCaseTitle(kase.title);
            } catch (e: any) {
                if (cancelled) return;
                setError(e?.response?.data?.detail || 'Failed to load operator overview');
            } finally {
                if (!cancelled) setLoadingExisting(false);
            }
        })();
        return () => { cancelled = true; };
    }, [id]);

    const runSynthesis = useCallback(async () => {
        if (!id || !macro) return;
        setSynthesizing(true);
        setError(null);
        try {
            const resp = await operatorService.synthesizeMacro(id, {
                ledger: macro.ledger,
                entities: macro.entities,
                cluster: macro.cluster,
                cluster_trace: clusterTrace,
                onchain_contexts: Object.keys(onchainContexts).length > 0 ? onchainContexts : undefined,
            });
            setSynthesis(resp);
            setActiveStep('synthesis');  // land on the result the user just paid for
            const critCount = resp.patterns.filter(p => p.severity === 'critical' || p.severity === 'high').length;
            toast.success(
                `Synthesis ready — ${resp.patterns.length} pattern${resp.patterns.length === 1 ? '' : 's'}` +
                (critCount > 0 ? ` (${critCount} high/critical)` : '') +
                (resp.ai_used ? ' · AI narrative attached' : ' · narrative from patterns (LLM unavailable)')
            );
        } catch (e: any) {
            const msg = e?.response?.data?.detail || 'Synthesis failed';
            setError(msg);
            toast.error(msg);
        } finally {
            setSynthesizing(false);
        }
    }, [id, macro, clusterTrace, onchainContexts]);

    const runClusterTrace = useCallback(async () => {
        if (!id) return;
        setTracing(true);
        setError(null);
        try {
            const resp = await operatorService.runClusterTrace(id, { max_members: traceMaxMembers });
            setClusterTrace(resp);
            setActiveStep('trace');  // land on the result the user just paid for
            toast.success(
                `Cluster trace done — ${resp.members_traced} wallet${resp.members_traced === 1 ? '' : 's'} traced · ${resp.totals.off_ramps_unique} off-ramp${resp.totals.off_ramps_unique === 1 ? '' : 's'} · ${resp.totals.high_risk_unique} red flag${resp.totals.high_risk_unique === 1 ? '' : 's'}`
            );
        } catch (e: any) {
            const msg = e?.response?.data?.detail || 'Cluster trace failed';
            setError(msg);
            toast.error(msg);
        } finally {
            setTracing(false);
        }
    }, [id, traceMaxMembers]);

    const runStages = useCallback(async () => {
        if (!id) return;
        setRunning(true);
        setError(null);
        try {
            const resp = await operatorService.runMacro(id);
            setMacro(resp);
            const cm = resp.cluster.members.length;
            const cps = resp.entities?.totals?.unique ?? 0;
            toast.success(
                `Macro built — ${cm} wallet${cm === 1 ? '' : 's'}, ${cps} counterpart${cps === 1 ? 'y' : 'ies'}, regime: ${resp.ledger?.regime?.label || 'unknown'}`
            );
        } catch (e: any) {
            const msg = e?.response?.data?.detail || 'Failed to run macro overview';
            setError(msg);
            toast.error(msg);
        } finally {
            setRunning(false);
        }
    }, [id]);

    if (!id) return null;

    const showResults = macro && macro.cluster;
    const partialMacro = showResults && (!macro!.ledger || !macro!.entities);

    return (
        <div className="p-6 max-w-7xl mx-auto space-y-6">
            <PageBreadcrumb
                items={[
                    { label: 'Dashboard', href: '/' },
                    { label: 'Cases', href: '/cases' },
                    { label: caseTitle || 'Case', href: id ? `/cases/${id}` : undefined },
                    { label: 'Macro Analysis' },
                ]}
            />
            <div className="flex items-center gap-2">
                <button
                    onClick={() => navigate(`/cases/${id}`)}
                    className="p-2 rounded hover:bg-secondary text-muted-foreground hover:text-foreground transition-all"
                    title="Back to case detail"
                >
                    <ArrowLeft className="w-4 h-4" />
                </button>
                <div className="flex-1 min-w-0">
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Operator Macro Overview</p>
                    <h1 className="text-xl font-bold truncate">{caseTitle || 'Case'}</h1>
                </div>
                {showResults && (
                    <button
                        onClick={runStages}
                        disabled={running}
                        className="flex items-center gap-1.5 px-3 h-9 bg-secondary hover:bg-secondary/80 text-sm rounded-md disabled:opacity-50"
                        title="Re-run Stages 0–2 (idempotent — manual edits preserved)"
                    >
                        <RefreshCw className={`w-4 h-4 ${running ? 'animate-spin' : ''}`} />
                        Re-run
                    </button>
                )}
            </div>

            {error && (
                <div className="rounded-md border border-rose-500/40 bg-rose-500/5 px-4 py-3 text-sm text-rose-300 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{error}</span>
                </div>
            )}

            {partialMacro && (
                <div className="rounded-md border border-amber-500/30 bg-amber-500/5 px-4 py-2.5 text-xs text-amber-300 flex items-center gap-2">
                    <Activity className="w-3.5 h-3.5" />
                    Cluster loaded from a previous run. Click <span className="font-bold">Re-run</span> to refresh ledger + entity attribution.
                </div>
            )}

            {loadingExisting ? (
                <div className="flex items-center justify-center p-12 text-muted-foreground">
                    <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
                </div>
            ) : showResults ? (
                <ResultsView
                    macro={macro!}
                    caseId={id}
                    clusterTrace={clusterTrace}
                    onRunClusterTrace={runClusterTrace}
                    tracing={tracing}
                    traceMaxMembers={traceMaxMembers}
                    onChangeMaxMembers={setTraceMaxMembers}
                    synthesis={synthesis}
                    onRunSynthesis={runSynthesis}
                    synthesizing={synthesizing}
                    activeStep={activeStep}
                    onChangeStep={setActiveStep}
                    onchainContexts={onchainContexts}
                    onRecordOnchainContext={recordOnchainContext}
                />
            ) : (
                <ExplainerCard onRun={runStages} running={running} />
            )}
        </div>
    );
};

// ── Explainer card (pre-run state) ─────────────────────────────────────

const ExplainerCard: React.FC<{ onRun: () => void; running: boolean }> = ({ onRun, running }) => {
    const bundledStages = STAGES.filter((s) => s.bundled);
    const totalEta = '~5–10 seconds';

    return (
        <div className="rounded-2xl border border-border bg-card p-8 space-y-6">
            <div className="flex items-start gap-4">
                <div className="p-3 rounded-xl bg-primary/10 text-primary">
                    <ScanLine className="w-7 h-7" />
                </div>
                <div className="flex-1 min-w-0">
                    <h2 className="text-2xl font-bold">Build a unified picture of this operator</h2>
                    <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                        A subpoena case usually involves one person operating multiple wallets across multiple chains.
                        This view ties them together so you can answer operator-level questions instead of address-level ones.
                    </p>
                </div>
            </div>

            <div className="rounded-lg border border-border bg-card/50 p-5 space-y-3">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-muted-foreground font-bold">
                    <Layers className="w-3.5 h-3.5" />
                    What "Run Stages 0–2" does
                </div>
                <ul className="space-y-2.5">
                    {bundledStages.map((s) => (
                        <li key={s.n} className="flex items-start gap-3">
                            <div className="mt-0.5 w-6 h-6 rounded-full bg-primary/15 text-primary text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                                {s.n}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-sm font-bold">{s.label}</span>
                                    <CostBadge cost={s.cost} />
                                    <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                                        <Clock className="w-3 h-3" />{s.eta}
                                    </span>
                                </div>
                                <p className="text-xs text-muted-foreground mt-0.5">{s.desc}</p>
                            </div>
                        </li>
                    ))}
                </ul>
                <div className="pt-2 flex items-center justify-between border-t border-border/50">
                    <p className="text-[11px] text-muted-foreground">
                        All bundled stages are deterministic and free — no third-party API spend.
                    </p>
                    <p className="text-[11px] font-bold text-emerald-400 flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />Total {totalEta}
                    </p>
                </div>
            </div>

            <div className="rounded-lg border border-border/40 bg-card/30 p-5 space-y-3">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-muted-foreground font-bold">
                    <Zap className="w-3.5 h-3.5" />
                    Optional follow-on stages (separate buttons after 0–2 completes)
                </div>
                <ul className="space-y-2">
                    {STAGES.filter((s) => !s.bundled).map((s) => (
                        <li key={s.n} className="flex items-center gap-3 text-xs">
                            <div className="w-5 h-5 rounded-full bg-secondary text-muted-foreground text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                                {s.n}
                            </div>
                            <span className="font-bold flex-shrink-0">{s.label}</span>
                            <CostBadge cost={s.cost} />
                            <span className="text-muted-foreground text-[10px] flex items-center gap-1">
                                <Clock className="w-3 h-3" />{s.eta}
                            </span>
                            <span className="text-muted-foreground truncate hidden md:inline">— {s.desc}</span>
                        </li>
                    ))}
                </ul>
            </div>

            <div className="flex justify-end">
                <button
                    onClick={onRun}
                    disabled={running}
                    className="flex items-center gap-2 px-6 h-12 bg-primary hover:bg-primary text-primary-foreground font-bold rounded-lg shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                    {running
                        ? <><Loader2 className="w-5 h-5 animate-spin" /> Running…</>
                        : <><ScanLine className="w-5 h-5" /> Run Stages 0–2</>}
                </button>
            </div>
        </div>
    );
};

const CostBadge: React.FC<{ cost: StageRow['cost'] }> = ({ cost }) => {
    if (cost === 'free') {
        return (
            <span className="px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 text-[9px] font-bold uppercase">
                Free
            </span>
        );
    }
    return (
        <span className="px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20 text-[9px] font-bold uppercase flex items-center gap-1">
            <Coins className="w-2.5 h-2.5" />
            {cost === 'paid_per_wallet' ? 'Paid / wallet' : 'Paid'}
        </span>
    );
};

// ── Results view ───────────────────────────────────────────────────────

interface ResultsViewProps {
    macro: MacroRunResponse;
    caseId: string;
    clusterTrace: ClusterTraceResponse | null;
    onRunClusterTrace: () => void;
    tracing: boolean;
    traceMaxMembers: number;
    onChangeMaxMembers: (n: number) => void;
    synthesis: MacroSynthesisResponse | null;
    onRunSynthesis: () => void;
    synthesizing: boolean;
    activeStep: StepKey;
    onChangeStep: (s: StepKey) => void;
    onchainContexts: Record<string, OnchainContextResponse>;
    onRecordOnchainContext: (chain: string, address: string, ctx: OnchainContextResponse) => void;
}

const ResultsView: React.FC<ResultsViewProps> = ({
    macro, caseId,
    clusterTrace, onRunClusterTrace, tracing, traceMaxMembers, onChangeMaxMembers,
    synthesis, onRunSynthesis, synthesizing,
    activeStep, onChangeStep,
    onchainContexts, onRecordOnchainContext,
}) => {
    const { ledger, entities, cluster } = macro;
    const canSynthesize = !!ledger && !!entities;

    const stepStatus: Record<StepKey, StepStatus> = {
        cluster:   cluster ? 'done' : 'pending',
        ledger:    ledger ? 'done' : 'pending',
        entities:  entities ? 'done' : 'pending',
        trace:     tracing ? 'running' : (clusterTrace ? 'done' : 'ready'),
        synthesis: synthesizing ? 'running' : (synthesis ? 'done' : (canSynthesize ? 'ready' : 'locked')),
    };

    return (
        <div className="space-y-6">
            <StepperNav activeKey={activeStep} statuses={stepStatus} onSelect={onChangeStep} />

            {activeStep === 'cluster' && (
                <ClusterResultsView
                    cluster={cluster}
                    caseId={caseId}
                    onchainContexts={onchainContexts}
                    onRecordOnchainContext={onRecordOnchainContext}
                />
            )}

            {activeStep === 'ledger' && (
                ledger ? <LedgerSection ledger={ledger} /> : <EmptyStepState stepLabel="Ledger" />
            )}

            {activeStep === 'entities' && (
                entities ? <EntitySection entities={entities} /> : <EmptyStepState stepLabel="Entities" />
            )}

            {activeStep === 'trace' && (
                <ClusterTraceSection
                    trace={clusterTrace}
                    onRun={onRunClusterTrace}
                    tracing={tracing}
                    clusterMemberCount={cluster.members.length}
                    maxMembers={traceMaxMembers}
                    onChangeMaxMembers={onChangeMaxMembers}
                />
            )}

            {activeStep === 'synthesis' && (
                <MacroSynthesisSection
                    synthesis={synthesis}
                    onRun={onRunSynthesis}
                    synthesizing={synthesizing}
                    hasClusterTrace={!!clusterTrace}
                    canSynthesize={canSynthesize}
                />
            )}
        </div>
    );
};

// ── Stepper navigation ────────────────────────────────────────────────

interface StepperNavProps {
    activeKey: StepKey;
    statuses: Record<StepKey, StepStatus>;
    onSelect: (k: StepKey) => void;
}

const StepperNav: React.FC<StepperNavProps> = ({ activeKey, statuses, onSelect }) => (
    <div className="rounded-xl border border-border bg-card/50 p-3">
        <div className="flex items-center gap-1 overflow-x-auto">
            {STEP_DEFS.map((s, i) => {
                // Connector after this chip is "filled" (emerald) when the
                // chip on its left has completed — this makes the trail
                // visually fill from left to right as the user progresses.
                const filledTrail = statuses[s.key] === 'done';
                return (
                    <React.Fragment key={s.key}>
                        <StepChip
                            def={s}
                            status={statuses[s.key]}
                            active={activeKey === s.key}
                            onClick={() => onSelect(s.key)}
                        />
                        {i < STEP_DEFS.length - 1 && (
                            <div
                                className={`flex-shrink-0 w-6 h-0.5 transition-colors ${
                                    filledTrail ? 'bg-emerald-500/60' : 'bg-border'
                                }`}
                                aria-hidden
                            />
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    </div>
);

interface StepChipProps {
    def: StepDef;
    status: StepStatus;
    active: boolean;
    onClick: () => void;
}

const StepChip: React.FC<StepChipProps> = ({ def, status, active, onClick }) => {
    const Icon = def.icon;
    const isLocked = status === 'locked';
    const isDone = status === 'done';
    const isRunning = status === 'running';

    // Tone for the chip background/border
    const toneActive    = 'bg-primary/15 border-primary/40 text-foreground';
    const toneDone      = 'bg-emerald-500/5 border-emerald-500/20 text-foreground hover:bg-emerald-500/10';
    const tonePending   = 'bg-card border-border text-muted-foreground hover:text-foreground hover:border-border/80';
    const toneReady     = 'bg-card border-border text-foreground hover:bg-secondary/50';
    const toneLocked    = 'bg-muted/20 border-border/50 text-muted-foreground/60 cursor-not-allowed';

    const tone = active ? toneActive
        : isLocked ? toneLocked
        : isDone ? toneDone
        : status === 'ready' ? toneReady
        : tonePending;

    // Number-circle tone — active gets blue, done gets emerald, locked stays muted
    const numTone = active ? 'bg-primary text-white'
        : isDone ? 'bg-emerald-500/80 text-white'
        : isLocked ? 'bg-muted text-muted-foreground/60'
        : 'bg-secondary text-foreground';

    return (
        <button
            type="button"
            onClick={isLocked ? undefined : onClick}
            disabled={isLocked}
            aria-current={active ? 'step' : undefined}
            data-testid={`step-chip-${def.key}`}
            className={`flex items-center gap-2 px-3 h-10 rounded-lg border text-sm font-bold transition-all flex-shrink-0 ${tone}`}
            title={
                isLocked
                    ? 'Run Stages 0–2 first to unlock this step'
                    : isRunning ? `${def.label} — running…`
                    : isDone ? `${def.label} — done (click to view)`
                    : `${def.label} — click to open`
            }
        >
            <span className={`w-6 h-6 rounded-full text-[11px] font-black flex items-center justify-center flex-shrink-0 ${numTone}`}>
                {def.n}
            </span>
            <Icon className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="hidden sm:inline">{def.label}</span>
            {def.cost === 'paid' && !isDone && !isRunning && (
                <Coins className="w-3 h-3 text-amber-400 flex-shrink-0" aria-label="paid step" />
            )}
            {isRunning && <Loader2 className="w-3.5 h-3.5 animate-spin text-primary flex-shrink-0" />}
            {isDone && !isRunning && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />}
            {isLocked && <Lock className="w-3 h-3 text-muted-foreground/60 flex-shrink-0" />}
        </button>
    );
};

// Empty state for ledger/entities steps when partial macro (cluster only) is loaded
const EmptyStepState: React.FC<{ stepLabel: string }> = ({ stepLabel }) => (
    <div className="rounded-lg border border-dashed border-border bg-card/30 p-8 text-center text-sm text-muted-foreground space-y-2">
        <Activity className="w-6 h-6 mx-auto text-muted-foreground/50" />
        <p>
            <span className="font-bold text-foreground">{stepLabel}</span> hasn't been populated yet.
        </p>
        <p className="text-xs">
            Click <span className="font-bold text-foreground">Re-run</span> in the page header to refresh ledger + entity attribution.
        </p>
    </div>
);

// ── Stage 2: cluster ───────────────────────────────────────────────────

interface ClusterResultsViewProps {
    cluster: OperatorCluster;
    caseId: string;
    onchainContexts: Record<string, OnchainContextResponse>;
    onRecordOnchainContext: (chain: string, address: string, ctx: OnchainContextResponse) => void;
}

const ClusterResultsView: React.FC<ClusterResultsViewProps> = ({ cluster, caseId, onchainContexts, onRecordOnchainContext }) => {
    const { kyc, members, confidence } = cluster;
    const byChain = useMemo(() => {
        const map: Record<string, OperatorClusterMember[]> = {};
        for (const m of members) {
            (map[m.chain] = map[m.chain] || []).push(m);
        }
        return map;
    }, [members]);

    const signalCounts = useMemo(() => {
        const c: Record<string, number> = {};
        for (const m of members) {
            for (const e of m.evidence) c[e.signal] = (c[e.signal] || 0) + 1;
        }
        return c;
    }, [members]);

    return (
        <div className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <OperatorCard kyc={kyc} confidence={confidence} memberCount={members.length} />
                <SignalCard signalCounts={signalCounts} />
                <ChainSpreadCard byChain={byChain} />
            </div>

            {Object.entries(byChain).map(([chain, ms]) => (
                <ChainGroup
                    key={chain}
                    chain={chain}
                    members={ms}
                    caseId={caseId}
                    onchainContexts={onchainContexts}
                    onRecordOnchainContext={onRecordOnchainContext}
                />
            ))}

            {members.length === 0 && (
                <div className="rounded-lg border border-border bg-card p-8 text-center text-sm text-muted-foreground">
                    No operator wallets clustered yet. Upload a forensic record (e.g. Binance subpoena) on the case detail
                    page, then re-run.
                </div>
            )}
        </div>
    );
};

const OperatorCard: React.FC<{
    kyc: OperatorCluster['kyc'];
    confidence: number;
    memberCount: number;
}> = ({ kyc, confidence, memberCount }) => (
    <div className="rounded-xl border border-primary/20 bg-primary/5 p-5 space-y-3">
        <div className="flex items-start justify-between">
            <div>
                <p className="text-[10px] uppercase tracking-widest text-primary font-bold">Operator</p>
                <h3 className="text-lg font-bold mt-0.5">{kyc.name || kyc.email || 'Unnamed operator'}</h3>
            </div>
            <div className="p-2 rounded-lg bg-primary/10 text-primary">
                <ShieldCheck className="w-5 h-5" />
            </div>
        </div>
        <div className="space-y-1 text-xs">
            {kyc.email && <p><span className="text-muted-foreground">Email:</span> <span className="text-primary break-all">{kyc.email}</span></p>}
            {kyc.exchange_user_id && (
                <p><span className="text-muted-foreground">{kyc.exchange_name || 'Exchange'} UID:</span>{' '}
                    <span className="font-mono">{kyc.exchange_user_id}</span></p>
            )}
            {kyc.phone && <p><span className="text-muted-foreground">Phone:</span> <span className="font-mono">{kyc.phone}</span></p>}
        </div>
        <div className="pt-2 border-t border-primary/20 flex items-center justify-between text-xs">
            <span><span className="text-muted-foreground">Wallets:</span> <span className="font-bold">{memberCount}</span></span>
            <span><span className="text-muted-foreground">Confidence:</span>{' '}
                <span className="font-bold text-primary">{(confidence * 100).toFixed(0)}%</span></span>
        </div>
    </div>
);

const SignalCard: React.FC<{ signalCounts: Record<string, number> }> = ({ signalCounts }) => {
    const labels: Record<string, { label: string; color: string }> = {
        kyc_join:          { label: 'KYC join',          color: 'text-emerald-400' },
        same_evm_address:  { label: 'Same EVM address',  color: 'text-primary' },
        btc_cospend:       { label: 'BTC co-spend',      color: 'text-amber-400' },
        manual_add:        { label: 'Manual add',        color: 'text-muted-foreground' },
    };
    const entries = Object.entries(signalCounts);
    return (
        <div className="rounded-xl border border-border bg-card p-5 space-y-3">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground font-bold">
                <Database className="w-3.5 h-3.5" /> Signals fired
            </div>
            {entries.length === 0 ? (
                <p className="text-xs text-muted-foreground italic">No signals yet</p>
            ) : (
                <ul className="space-y-1.5 text-xs">
                    {entries.map(([sig, count]) => {
                        const meta = labels[sig] || { label: sig, color: 'text-muted-foreground' };
                        return (
                            <li key={sig} className="flex items-center justify-between">
                                <span className={`font-bold ${meta.color}`}>{meta.label}</span>
                                <span className="tabular-nums text-foreground">{count}</span>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

const ChainSpreadCard: React.FC<{ byChain: Record<string, OperatorClusterMember[]> }> = ({ byChain }) => {
    const entries = Object.entries(byChain).sort((a, b) => b[1].length - a[1].length);
    return (
        <div className="rounded-xl border border-border bg-card p-5 space-y-3">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground font-bold">
                <Layers className="w-3.5 h-3.5" /> Chain spread
            </div>
            {entries.length === 0 ? (
                <p className="text-xs text-muted-foreground italic">No chains</p>
            ) : (
                <div className="flex flex-wrap gap-1.5">
                    {entries.map(([chain, ms]) => (
                        <span key={chain} className="px-2 py-1 rounded bg-secondary text-xs font-bold">
                            {chain} <span className="text-muted-foreground tabular-nums">·{ms.length}</span>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
};

// Chain groups are collapsed by default — Mohammed's case has 73 TRX wallets
// and the full list pushes everything else off-screen. Expand on click.
interface ChainGroupProps {
    chain: string;
    members: OperatorClusterMember[];
    caseId: string;
    onchainContexts: Record<string, OnchainContextResponse>;
    onRecordOnchainContext: (chain: string, address: string, ctx: OnchainContextResponse) => void;
    defaultOpen?: boolean;
}

const ChainGroup: React.FC<ChainGroupProps> = ({ chain, members, caseId, onchainContexts, onRecordOnchainContext, defaultOpen = false }) => {
    const [open, setOpen] = useState(defaultOpen);
    const subpoenaCount = members.filter(m =>
        m.evidence.some(e => e.signal === 'kyc_join' || e.signal === 'manual_add')
    ).length;
    return (
        <div className="rounded-xl border border-border bg-card overflow-hidden">
            <button
                type="button"
                onClick={() => setOpen(v => !v)}
                className="w-full px-4 py-2 bg-secondary/50 border-b border-border flex items-center justify-between hover:bg-secondary/70 transition-colors text-left"
                aria-expanded={open}
            >
                <span className="flex items-center gap-2">
                    {open
                        ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground" />
                        : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />}
                    <span className="text-sm font-bold">{chain}</span>
                </span>
                <span className="flex items-center gap-3 text-[10px] tabular-nums">
                    {subpoenaCount > 0 && (
                        <span className="text-emerald-400 flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3" />
                            {subpoenaCount} subpoena
                        </span>
                    )}
                    <span className="text-muted-foreground">{members.length} wallet{members.length === 1 ? '' : 's'}</span>
                </span>
            </button>
            {open && (
                <div className="divide-y divide-border">
                    {members.map((m) => (
                        <MemberRow
                            key={`${m.chain}:${m.address}`}
                            member={m}
                            caseId={caseId}
                            initialOnchain={onchainContexts[`${m.chain}:${m.address}`] || null}
                            onPulled={(ctx) => onRecordOnchainContext(m.chain, m.address, ctx)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

interface MemberRowProps {
    member: OperatorClusterMember;
    caseId: string;
    initialOnchain: OnchainContextResponse | null;
    onPulled: (ctx: OnchainContextResponse) => void;
}

const MemberRow: React.FC<MemberRowProps> = ({ member, caseId, initialOnchain, onPulled }) => {
    const subpoenaAttested = member.evidence.some((e) => e.signal === 'kyc_join' || e.signal === 'manual_add');
    const [onchain, setOnchain] = useState<OnchainContextResponse | null>(initialOnchain);
    const [loading, setLoading] = useState(false);
    // If we have a cached pull from a parent map, default to expanded so the
    // user immediately sees the result they paid for; otherwise collapsed.
    const [expanded, setExpanded] = useState(!!initialOnchain);
    const [err, setErr] = useState<string | null>(null);

    const handlePullContext = async () => {
        if (loading) return;
        // If we already have it, just toggle the expansion
        if (onchain) {
            setExpanded((v) => !v);
            return;
        }
        setLoading(true);
        setErr(null);
        try {
            const resp = await operatorService.fetchOnchainContext(caseId, member.chain, member.address);
            setOnchain(resp);
            setExpanded(true);
            // Push up to the page-level map so Stage 5 can use it
            onPulled(resp);
            const wc = resp.window_comparison;
            const outside = wc.pre_window + wc.post_window;
            toast.success(
                `${resp.onchain.tx_count_returned} on-chain tx${resp.onchain.tx_count_returned === 1 ? '' : 's'} pulled · ${outside} outside subpoena window · ${resp.new_counterparties.length} new counterpart${resp.new_counterparties.length === 1 ? 'y' : 'ies'}`
            );
        } catch (e: any) {
            const msg = e?.response?.data?.detail || 'On-chain fetch failed';
            setErr(msg);
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <div className="px-4 py-3 hover:bg-secondary/30 flex items-center gap-3 text-xs">
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${subpoenaAttested ? 'bg-emerald-400' : 'bg-primary'}`}
                     title={subpoenaAttested ? 'Subpoena-attested' : 'On-chain inferred'} />
                <span className="font-mono break-all flex-1 min-w-0">{member.address}</span>
                <span className="font-bold tabular-nums w-12 text-right">{(member.confidence * 100).toFixed(0)}%</span>
                <div className="flex flex-wrap gap-1 justify-end">
                    {member.evidence.map((e, i) => (
                        <span key={i} className="px-1.5 py-0.5 rounded bg-secondary text-[9px] font-bold uppercase tracking-tight"
                              title={JSON.stringify(e.details ?? {}, null, 2)}>
                            {e.signal === 'kyc_join'         ? 'KYC' :
                             e.signal === 'same_evm_address' ? 'Same EVM' :
                             e.signal === 'btc_cospend'      ? 'Co-spend' :
                             e.signal === 'manual_add'       ? 'Manual' :
                             e.signal}
                        </span>
                    ))}
                </div>
                {subpoenaAttested && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />}
                <button
                    onClick={handlePullContext}
                    disabled={loading}
                    className={`flex items-center gap-1 px-2 h-7 text-[10px] font-bold rounded border transition-all flex-shrink-0 ${
                        onchain
                            ? 'bg-amber-500/10 text-amber-400 border-amber-500/30 hover:bg-amber-500/20'
                            : 'bg-secondary text-muted-foreground border-border hover:bg-secondary/80 hover:text-foreground'
                    } disabled:opacity-50`}
                    title={
                        onchain
                            ? 'Toggle on-chain context details'
                            : 'Pull on-chain activity for this wallet (paid — one chain-API call, ~3–10s)'
                    }
                >
                    {loading
                        ? <Loader2 className="w-3 h-3 animate-spin" />
                        : onchain
                            ? (expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)
                            : <Radio className="w-3 h-3" />}
                    {loading ? 'Pulling…' : onchain ? 'On-chain' : 'Pull on-chain'}
                    {!onchain && (
                        <span className="ml-1 px-1 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20 text-[8px] font-black tracking-wider">$</span>
                    )}
                </button>
            </div>

            {err && (
                <div className="px-4 pb-3 text-[10px] text-rose-400 italic">{err}</div>
            )}

            {expanded && onchain && (
                <OnchainContextDetail ctx={onchain} />
            )}
        </>
    );
};

const OnchainContextDetail: React.FC<{ ctx: OnchainContextResponse }> = ({ ctx }) => {
    const wc = ctx.window_comparison;
    const outside = wc.pre_window + wc.post_window;
    return (
        <div className="px-4 py-3 bg-secondary/20 border-t border-border space-y-3 text-xs">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <Stat label="Tx returned" value={ctx.onchain.tx_count_returned} />
                <Stat label="In / Out" value={`${ctx.onchain.in_count} / ${ctx.onchain.out_count}`} />
                <Stat
                    label="Outside subpoena"
                    value={wc.subpoena_window_known ? `${outside} (${wc.percentage_outside}%)` : 'no window'}
                    accent={outside > 0 ? 'text-amber-400' : undefined}
                />
                <Stat label="New counterparties" value={ctx.new_counterparties.length} />
            </div>

            {wc.subpoena_window_known && (
                <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                    <Clock className="w-3 h-3" />
                    Subpoena window: {ctx.subpoena_window.first?.slice(0, 10)} → {ctx.subpoena_window.last?.slice(0, 10)} ({ctx.subpoena_window.link_count} links).
                    Pre-window: {wc.pre_window} · Inside: {wc.inside_window} · <span className={wc.post_window > 0 ? 'text-amber-400 font-bold' : ''}>Post-window: {wc.post_window}</span>
                </div>
            )}

            {ctx.new_counterparties.length > 0 && (
                <div>
                    <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1.5">
                        New counterparties (not in subpoena, top {ctx.new_counterparties.length})
                    </p>
                    <div className="space-y-1">
                        {ctx.new_counterparties.map((c) => (
                            <div key={`${c.chain}:${c.address}`} className="flex items-center gap-2 px-2 py-1 rounded bg-card hover:bg-card/80">
                                <span className="px-1.5 py-0.5 rounded bg-secondary text-[9px] font-bold uppercase tracking-tight w-12 text-center flex-shrink-0">
                                    {c.chain}
                                </span>
                                {c.label && <span className="font-bold text-foreground">{c.label}</span>}
                                {c.category && (
                                    <span className="px-1.5 py-0.5 rounded bg-primary/10 text-primary border border-primary/20 text-[9px] font-bold uppercase">
                                        {c.category}
                                    </span>
                                )}
                                {c.risk_level && (c.risk_level === 'critical' || c.risk_level === 'high') && (
                                    <span className="px-1.5 py-0.5 rounded bg-rose-500/10 text-rose-400 border border-rose-500/20 text-[9px] font-bold uppercase">
                                        {c.risk_level}
                                    </span>
                                )}
                                <span className="font-mono text-[10px] text-muted-foreground break-all flex-1 min-w-0">{c.address}</span>
                                <span className="text-[10px] tabular-nums font-bold w-8 text-right">{c.tx_count}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <p className="text-[9px] text-muted-foreground italic">
                Source: {ctx.data_source || 'chain'} · fetched {ctx.fetched_at?.slice(0, 19).replace('T', ' ')} UTC
            </p>
        </div>
    );
};

const Stat: React.FC<{ label: string; value: React.ReactNode; accent?: string }> = ({ label, value, accent }) => (
    <div className="bg-card border border-border rounded p-2">
        <p className="text-[9px] uppercase tracking-widest text-muted-foreground font-bold">{label}</p>
        <p className={`text-sm font-bold tabular-nums ${accent || ''}`}>{value}</p>
    </div>
);

// ── Stage 0: ledger ─────────────────────────────────────────────────────

const REGIME_META: Record<string, { label: string; color: string; bg: string }> = {
    accumulator:   { label: 'Accumulator',   color: 'text-emerald-300', bg: 'bg-emerald-500/10 border-emerald-500/30' },
    distributor:   { label: 'Distributor',   color: 'text-rose-300',    bg: 'bg-rose-500/10 border-rose-500/30' },
    pass_through:  { label: 'Pass-through',  color: 'text-primary',    bg: 'bg-primary/10 border-primary/30' },
    low_activity:  { label: 'Low activity',  color: 'text-muted-foreground', bg: 'bg-muted/20 border-border' },
    mixed:         { label: 'Mixed',         color: 'text-amber-300',   bg: 'bg-amber-500/10 border-amber-500/30' },
};

const LedgerSection: React.FC<{ ledger: LedgerSummary }> = ({ ledger }) => {
    const regimeMeta = REGIME_META[ledger.regime.label] || REGIME_META.mixed;
    const tp = ledger.time_profile;
    return (
        <section className="space-y-4">
            <h2 className="text-sm font-black uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                <Activity className="w-4 h-4" /> Ledger summary
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className={`rounded-xl border p-5 space-y-2 ${regimeMeta.bg}`}>
                    <p className="text-[10px] uppercase tracking-widest font-bold opacity-70">Regime</p>
                    <p className={`text-lg font-bold ${regimeMeta.color}`}>{regimeMeta.label}</p>
                    <p className="text-xs text-muted-foreground">{ledger.regime.explanation}</p>
                </div>
                <div className="rounded-xl border border-border bg-card p-5 space-y-2">
                    <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Volume</p>
                    <div className="flex items-baseline gap-2">
                        <span className="text-2xl font-bold tabular-nums">{ledger.totals.tx_count}</span>
                        <span className="text-xs text-muted-foreground">subpoena links</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs pt-1">
                        <span className="flex items-center gap-1 text-emerald-400">
                            <TrendingDown className="w-3 h-3" />
                            <span className="tabular-nums">{ledger.totals.deposits}</span> in
                        </span>
                        <span className="flex items-center gap-1 text-rose-400">
                            <TrendingUp className="w-3 h-3" />
                            <span className="tabular-nums">{ledger.totals.withdrawals}</span> out
                        </span>
                        <span className="text-muted-foreground">·</span>
                        <span className="text-muted-foreground tabular-nums">{ledger.totals.chains} chain{ledger.totals.chains === 1 ? '' : 's'}</span>
                    </div>
                </div>
                <div className="rounded-xl border border-border bg-card p-5 space-y-2">
                    <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Time profile</p>
                    {tp.first_seen && tp.last_seen ? (
                        <>
                            <p className="text-xs">
                                <span className="text-muted-foreground">From </span>
                                <span className="font-mono">{formatShortDate(tp.first_seen)}</span>
                                <span className="text-muted-foreground"> to </span>
                                <span className="font-mono">{formatShortDate(tp.last_seen)}</span>
                            </p>
                            <p className="text-xs text-muted-foreground tabular-nums">
                                {tp.days_active} day{tp.days_active === 1 ? '' : 's'} · {tp.txs_per_day}/day average
                            </p>
                        </>
                    ) : (
                        <p className="text-xs text-muted-foreground italic">No timestamps in scope</p>
                    )}
                </div>
            </div>

            {ledger.per_chain.length > 0 && (
                <PerChainTable rows={ledger.per_chain} />
            )}
        </section>
    );
};

// ── Per-chain ledger breakdown with sortable columns ───────────────────
// Click any column header to sort by that field (toggle asc/desc).
// "In" / "Out" use the same arrow icons as the Node Inspector Transfers tab.
type PerChainSortKey = 'chain' | 'tx_count' | 'deposits' | 'withdrawals' | 'self_addresses' | 'in_counterparties' | 'out_counterparties' | 'first_seen' | 'last_seen';

const PerChainTable: React.FC<{ rows: import('../services/api').LedgerPerChain[] }> = ({ rows }) => {
    const [sortKey, setSortKey] = useState<PerChainSortKey>('tx_count');
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

    const sorted = useMemo(() => {
        const s = [...rows];
        s.sort((a, b) => {
            const av = (a as any)[sortKey];
            const bv = (b as any)[sortKey];
            if (av == null && bv == null) return 0;
            if (av == null) return sortDir === 'asc' ? -1 : 1;
            if (bv == null) return sortDir === 'asc' ? 1 : -1;
            if (typeof av === 'number' && typeof bv === 'number') {
                return sortDir === 'asc' ? av - bv : bv - av;
            }
            const as = String(av);
            const bs = String(bv);
            return sortDir === 'asc' ? as.localeCompare(bs) : bs.localeCompare(as);
        });
        return s;
    }, [rows, sortKey, sortDir]);

    const handleSort = (k: PerChainSortKey) => {
        if (k === sortKey) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortKey(k);
            // numeric columns default to descending (largest first), text/date asc
            const numeric: PerChainSortKey[] = ['tx_count', 'deposits', 'withdrawals', 'self_addresses', 'in_counterparties', 'out_counterparties'];
            setSortDir(numeric.includes(k) ? 'desc' : 'asc');
        }
    };

    const SortHeader: React.FC<{ k: PerChainSortKey; label: React.ReactNode; align?: 'left' | 'right'; title?: string; className?: string }> = ({ k, label, align = 'right', title, className = '' }) => {
        const isActive = sortKey === k;
        return (
            <th className={`px-3 py-2 ${align === 'left' ? 'text-left' : 'text-right'} ${className}`}>
                <button
                    type="button"
                    onClick={() => handleSort(k)}
                    title={title || (typeof label === 'string' ? `Sort by ${label}` : 'Sort')}
                    className={`inline-flex items-center gap-1 ${align === 'right' ? 'flex-row-reverse' : ''} hover:text-foreground transition-colors ${isActive ? 'text-foreground' : ''}`}
                >
                    {label}
                    {isActive
                        ? (sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)
                        : <span className="w-3 h-3 opacity-30"><ChevronDown className="w-3 h-3" /></span>}
                </button>
            </th>
        );
    };

    return (
        <div className="rounded-xl border border-border bg-card overflow-hidden">
            <div className="px-4 py-2 bg-secondary/50 border-b border-border text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
                Per-chain breakdown — click a column to sort
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-xs">
                    <thead>
                        <tr className="text-[9px] uppercase tracking-tight text-muted-foreground border-b border-border/50">
                            <SortHeader k="chain" label="Chain" align="left" className="px-4" />
                            <SortHeader k="tx_count" label="Links" title="Subpoena tx links" />
                            <SortHeader
                                k="deposits"
                                label={<span className="inline-flex items-center gap-1 text-emerald-400"><ArrowDownLeft className="w-3 h-3" /> In</span>}
                                title="Inbound (received)"
                            />
                            <SortHeader
                                k="withdrawals"
                                label={<span className="inline-flex items-center gap-1 text-rose-400"><ArrowUpRight className="w-3 h-3" /> Out</span>}
                                title="Outbound (sent)"
                            />
                            <SortHeader k="self_addresses" label="Self addrs" title="Distinct operator-side addresses on this chain" />
                            <SortHeader k="in_counterparties" label="In CPs" title="Unique inbound counterparties (senders → operator)" />
                            <SortHeader k="out_counterparties" label="Out CPs" title="Unique outbound counterparties (operator → recipients). May be 0 if the source subpoena only contained inflows." />
                            <SortHeader k="first_seen" label="First" title="Earliest tx timestamp on this chain in the subpoena window" />
                            <SortHeader k="last_seen" label="Last" title="Latest tx timestamp on this chain in the subpoena window" />
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                        {sorted.map((c) => (
                            <tr key={c.chain} className="hover:bg-secondary/30">
                                <td className="px-4 py-2 font-bold">{c.chain}</td>
                                <td className="px-3 py-2 text-right tabular-nums">{c.tx_count}</td>
                                <td className="px-3 py-2 text-right tabular-nums text-emerald-400">{c.deposits}</td>
                                <td className="px-3 py-2 text-right tabular-nums text-rose-400">{c.withdrawals}</td>
                                <td className="px-3 py-2 text-right tabular-nums">{c.self_addresses}</td>
                                <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{c.in_counterparties}</td>
                                <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{c.out_counterparties}</td>
                                <td className="px-3 py-2 text-right tabular-nums text-muted-foreground font-mono">{c.first_seen ? formatShortDate(c.first_seen) : '—'}</td>
                                <td className="px-3 py-2 text-right tabular-nums text-muted-foreground font-mono">{c.last_seen ? formatShortDate(c.last_seen) : '—'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const formatShortDate = (iso: string) => {
    try {
        return new Date(iso).toISOString().slice(0, 10);
    } catch {
        return iso;
    }
};

// ── Stage 1: entity attribution ─────────────────────────────────────────

// Cap how many unlabelled addresses we ship to the live-API enrichment job in
// one click. The backend fans out to TronScan/GoPlus/Etherscan/etc. with
// rate limits and concurrency caps; >50 addresses can take minutes.
const _ENRICHMENT_BATCH_CAP = 50;

const EntitySection: React.FC<{ entities: EntityAttribution }> = ({ entities }) => {
    const labelled = entities.counterparties.filter((c) => c.label || c.category);
    const unlabelled = entities.counterparties.filter((c) => !c.label && !c.category);
    const topUnlabelled = unlabelled.slice(0, 10);

    // Live entity-lookup job state (Stage 1 → on-demand enrichment via existing
    // entityEnrichmentService — TronScan / GoPlus / Etherscan / Crystal). We
    // poll until done; results persist into the entity DB so the next macro
    // Re-run picks up the new labels automatically.
    const [enriching, setEnriching] = useState(false);
    const [enrichJob, setEnrichJob] = useState<{
        total: number;
        found: number;
        current: string | null;
        status: 'running' | 'complete' | 'error';
    } | null>(null);
    const [enrichErr, setEnrichErr] = useState<string | null>(null);

    const runEntityLookup = useCallback(async () => {
        if (enriching || unlabelled.length === 0) return;
        setEnrichErr(null);
        setEnriching(true);
        const batch = unlabelled.slice(0, _ENRICHMENT_BATCH_CAP).map(c => ({
            chain: c.chain,
            address: c.address,
        }));
        try {
            const { job_id, total } = await entityEnrichmentService.startEnrichment(batch);
            setEnrichJob({ total, found: 0, current: null, status: 'running' });
            // Poll every 1.5s up to ~3 minutes
            const startedAt = Date.now();
            // eslint-disable-next-line no-constant-condition
            while (true) {
                await new Promise(r => setTimeout(r, 1500));
                const s = await entityEnrichmentService.getStatus(job_id);
                setEnrichJob({
                    total: s.total,
                    found: s.found,
                    current: s.current_provider,
                    status: s.status,
                });
                if (s.status === 'complete' || s.status === 'error') {
                    if (s.status === 'error') {
                        setEnrichErr(s.error || 'Enrichment job failed');
                        toast.error(s.error || 'Entity lookup failed');
                    } else {
                        toast.success(
                            `Entity lookup done — ${s.found}/${s.total} new label${s.found === 1 ? '' : 's'} found. ` +
                            `Click Re-run in the page header to refresh attribution.`
                        );
                    }
                    break;
                }
                if (Date.now() - startedAt > 180_000) {
                    setEnrichErr('Enrichment timed out after 3 minutes; partial results may have persisted.');
                    toast.warning('Entity lookup timed out');
                    break;
                }
            }
        } catch (e: any) {
            const msg = e?.response?.data?.detail || e?.message || 'Failed to start entity lookup';
            setEnrichErr(msg);
            toast.error(msg);
        } finally {
            setEnriching(false);
        }
    }, [enriching, unlabelled]);

    return (
        <section className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <h2 className="text-sm font-black uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                    <Tag className="w-4 h-4" /> Entity attribution
                </h2>
                <div className="flex items-center gap-3">
                    <p className="text-[11px] text-muted-foreground tabular-nums">
                        {entities.totals.labelled} labelled / {entities.totals.unique} unique
                    </p>
                    <button
                        onClick={runEntityLookup}
                        disabled={enriching || unlabelled.length === 0}
                        className="flex items-center gap-1.5 px-3 h-9 bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 font-bold text-xs rounded-md border border-amber-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
                        title={
                            unlabelled.length === 0
                                ? 'All counterparties already labelled — nothing to enrich'
                                : `Run live entity lookup on the top ${Math.min(unlabelled.length, _ENRICHMENT_BATCH_CAP)} unlabelled counterpart${unlabelled.length === 1 ? 'y' : 'ies'} via TronScan / GoPlus / Etherscan. Results persist to the entity DB.`
                        }
                    >
                        {enriching
                            ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Looking up…</>
                            : <><Database className="w-3.5 h-3.5" /> Run Entity Lookup</>}
                        <span className="ml-1 px-1 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20 text-[8px] font-black tracking-wider">$</span>
                    </button>
                </div>
            </div>

            {enrichJob && (
                <div className={`rounded-md border px-3 py-2 text-xs flex items-center gap-2 ${
                    enrichJob.status === 'complete'
                        ? 'bg-emerald-500/5 border-emerald-500/30 text-emerald-300'
                        : enrichJob.status === 'error'
                            ? 'bg-rose-500/5 border-rose-500/30 text-rose-300'
                            : 'bg-amber-500/5 border-amber-500/30 text-amber-300'
                }`}>
                    {enrichJob.status === 'running' && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                    {enrichJob.status === 'complete' && <CheckCircle2 className="w-3.5 h-3.5" />}
                    {enrichJob.status === 'error' && <AlertTriangle className="w-3.5 h-3.5" />}
                    <span className="tabular-nums">
                        {enrichJob.status === 'running' && (enrichJob.current ? `Querying ${enrichJob.current}…` : 'Starting…')}
                        {' '}
                        {enrichJob.found}/{enrichJob.total} new labels found
                        {enrichJob.status === 'complete' && ' — click Re-run in the header to refresh attribution.'}
                    </span>
                </div>
            )}

            {enrichErr && (
                <p className="text-[10px] text-rose-400 italic">{enrichErr}</p>
            )}

            {labelled.length === 0 && (
                <div className="rounded-lg border border-border bg-card p-6 text-center text-xs text-muted-foreground">
                    No counterparties matched the entity database. Click <span className="font-bold text-foreground">Run Entity Lookup</span> above
                    to query live external sources (TronScan / GoPlus / Etherscan).
                </div>
            )}

            {labelled.length > 0 && (
                <div className="rounded-xl border border-border bg-card overflow-hidden">
                    <div className="px-4 py-2 bg-secondary/50 border-b border-border text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
                        Labelled counterparties
                    </div>
                    <div className="divide-y divide-border">
                        {labelled.map((c) => (
                            <CounterpartyRow key={`${c.chain}:${c.address}`} cp={c} />
                        ))}
                    </div>
                </div>
            )}

            {topUnlabelled.length > 0 && (
                <details className="rounded-xl border border-border/50 bg-card/30 overflow-hidden">
                    <summary className="cursor-pointer px-4 py-2 bg-secondary/30 text-[10px] uppercase tracking-widest font-bold text-muted-foreground hover:bg-secondary/50">
                        Top unlabelled counterparties ({entities.totals.unlabelled} total · showing {topUnlabelled.length})
                    </summary>
                    <div className="divide-y divide-border">
                        {topUnlabelled.map((c) => (
                            <CounterpartyRow key={`${c.chain}:${c.address}`} cp={c} />
                        ))}
                    </div>
                </details>
            )}
        </section>
    );
};

const CounterpartyRow: React.FC<{ cp: LabelledCounterparty }> = ({ cp }) => {
    const roleColor = cp.role === 'sender' ? 'text-emerald-400' : cp.role === 'recipient' ? 'text-rose-400' : 'text-primary';
    return (
        <div className="px-4 py-3 hover:bg-secondary/30 flex items-center gap-3 text-xs">
            <span className="px-1.5 py-0.5 rounded bg-secondary text-[9px] font-bold uppercase tracking-tight w-12 text-center flex-shrink-0">
                {cp.chain}
            </span>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    {cp.label && <span className="font-bold text-foreground">{cp.label}</span>}
                    {cp.category && (
                        <span className="px-1.5 py-0.5 rounded bg-primary/10 text-primary border border-primary/20 text-[9px] font-bold uppercase">
                            {cp.category}
                        </span>
                    )}
                    {cp.risk_level && (
                        <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${
                            cp.risk_level === 'critical' || cp.risk_level === 'high'
                                ? 'bg-rose-500/10 text-rose-400 border border-rose-500/20'
                                : 'bg-secondary text-muted-foreground'
                        }`}>
                            {cp.risk_level}
                        </span>
                    )}
                </div>
                <p className="font-mono text-[10px] text-muted-foreground break-all mt-0.5">{cp.address}</p>
            </div>
            <span className={`text-[10px] uppercase font-bold tracking-tight w-16 text-right ${roleColor}`}>
                {cp.role}
            </span>
            <span className="text-xs font-bold tabular-nums w-12 text-right">{cp.tx_count}</span>
        </div>
    );
};

// ── Stage 4: cluster-level trace ────────────────────────────────────────

interface ClusterTraceSectionProps {
    trace: ClusterTraceResponse | null;
    onRun: () => void;
    tracing: boolean;
    clusterMemberCount: number;
    maxMembers: number;
    onChangeMaxMembers: (n: number) => void;
}

const ClusterTraceSection: React.FC<ClusterTraceSectionProps> = ({
    trace, onRun, tracing, clusterMemberCount, maxMembers, onChangeMaxMembers,
}) => {
    return (
        <section className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <h2 className="text-sm font-black uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                    <Network className="w-4 h-4" /> Stage 4 — Cluster trace
                    <span className="px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20 text-[9px] font-bold uppercase flex items-center gap-1">
                        <Coins className="w-2.5 h-2.5" />Paid
                    </span>
                </h2>
                <div className="flex items-center gap-2 text-xs">
                    {!trace && (
                        <label className="flex items-center gap-1.5 text-muted-foreground">
                            Top
                            <select
                                value={maxMembers}
                                onChange={(e) => onChangeMaxMembers(parseInt(e.target.value, 10))}
                                disabled={tracing}
                                className="bg-secondary border border-border rounded px-2 py-1 text-xs font-bold tabular-nums"
                            >
                                {[5, 10, 15, 25].map((n) => (
                                    <option key={n} value={n}>{n}</option>
                                ))}
                            </select>
                            of {clusterMemberCount} wallets
                        </label>
                    )}
                    <button
                        onClick={onRun}
                        disabled={tracing}
                        className="flex items-center gap-1.5 px-3 h-9 bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 font-bold text-sm rounded-md border border-amber-500/30 disabled:opacity-50"
                        title={trace ? 'Re-run cluster trace' : 'Fan out Stage 3 across the top-N cluster members'}
                    >
                        {tracing
                            ? <><Loader2 className="w-4 h-4 animate-spin" /> Tracing…</>
                            : trace
                                ? <><RefreshCw className="w-4 h-4" /> Re-trace</>
                                : <><Network className="w-4 h-4" /> Run cluster trace</>}
                    </button>
                </div>
            </div>

            {!trace && !tracing && (
                <div className="rounded-lg border border-dashed border-border bg-card/30 p-6 text-center text-xs text-muted-foreground">
                    Pulls on-chain activity for the top-{maxMembers} cluster members in parallel and consolidates
                    off-ramps + high-risk hits + new counterparties across the operator's wallets.
                    Bounded at 5 concurrent chain calls — typically 10–60s wall-time.
                </div>
            )}

            {trace && <ClusterTraceResults trace={trace} />}
        </section>
    );
};

const ClusterTraceResults: React.FC<{ trace: ClusterTraceResponse }> = ({ trace }) => {
    const wt = trace.window_totals;
    const totalCategorised = wt.pre_window_total + wt.inside_window_total + wt.post_window_total;
    const pctOutside = totalCategorised > 0
        ? Math.round((wt.pre_window_total + wt.post_window_total) / totalCategorised * 1000) / 10
        : 0;

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat label="Wallets traced" value={`${trace.members_traced}/${trace.members_requested}`} />
                <Stat label="On-chain txs" value={trace.onchain_totals.tx_count_returned} />
                <Stat
                    label="Outside subpoena"
                    value={`${wt.pre_window_total + wt.post_window_total} (${pctOutside}%)`}
                    accent={(wt.pre_window_total + wt.post_window_total) > 0 ? 'text-amber-400' : undefined}
                />
                <Stat
                    label="Red-flag hits"
                    value={trace.totals.high_risk_unique}
                    accent={trace.totals.high_risk_unique > 0 ? 'text-rose-400' : undefined}
                />
            </div>

            {trace.high_risk_hits.length > 0 && (
                <div className="rounded-xl border border-rose-500/30 bg-rose-500/5 overflow-hidden">
                    <div className="px-4 py-2 bg-rose-500/10 border-b border-rose-500/20 flex items-center gap-2">
                        <Flame className="w-4 h-4 text-rose-400" />
                        <span className="text-sm font-bold text-rose-300">
                            High-risk counterparties ({trace.high_risk_hits.length})
                        </span>
                    </div>
                    <div className="divide-y divide-rose-500/10">
                        {trace.high_risk_hits.map((c) => (
                            <ConsolidatedRow key={`${c.chain}:${c.address}`} cp={c} accent="rose" />
                        ))}
                    </div>
                </div>
            )}

            {trace.off_ramps.length > 0 && (
                <div className="rounded-xl border border-border bg-card overflow-hidden">
                    <div className="px-4 py-2 bg-secondary/50 border-b border-border flex items-center gap-2">
                        <ArrowUpRight className="w-4 h-4 text-emerald-400" />
                        <span className="text-sm font-bold">
                            Off-ramps consolidated ({trace.off_ramps.length})
                        </span>
                        <span className="text-[10px] text-muted-foreground">
                            — exchanges/CEXs reached across all traced wallets
                        </span>
                    </div>
                    <div className="divide-y divide-border">
                        {trace.off_ramps.map((c) => (
                            <ConsolidatedRow key={`${c.chain}:${c.address}`} cp={c} />
                        ))}
                    </div>
                </div>
            )}

            {trace.per_member_summary.length > 0 && (
                <details className="rounded-xl border border-border/50 bg-card/30 overflow-hidden">
                    <summary className="cursor-pointer px-4 py-2 bg-secondary/30 text-[10px] uppercase tracking-widest font-bold text-muted-foreground hover:bg-secondary/50">
                        Per-member breakdown ({trace.per_member_summary.length})
                    </summary>
                    <table className="w-full text-xs">
                        <thead>
                            <tr className="text-[9px] uppercase tracking-tight text-muted-foreground">
                                <th className="px-4 py-2 text-left">Chain</th>
                                <th className="px-3 py-2 text-left">Address</th>
                                <th className="px-3 py-2 text-right">On-chain</th>
                                <th className="px-3 py-2 text-right">Post-win</th>
                                <th className="px-3 py-2 text-right">% out</th>
                                <th className="px-3 py-2 text-right">New CPs</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {trace.per_member_summary.map((m) => (
                                <tr key={`${m.chain}:${m.address}`} className="hover:bg-secondary/30">
                                    <td className="px-4 py-2 font-bold">{m.chain}</td>
                                    <td className="px-3 py-2 font-mono text-[10px] truncate max-w-[300px]">{m.address}</td>
                                    <td className="px-3 py-2 text-right tabular-nums">{m.onchain_tx_count}</td>
                                    <td className={`px-3 py-2 text-right tabular-nums ${m.post_window > 0 ? 'text-amber-400 font-bold' : ''}`}>{m.post_window}</td>
                                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{m.percentage_outside}%</td>
                                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{m.new_counterparty_count}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </details>
            )}

            {trace.consolidated_counterparties.length > 0 && (
                <details className="rounded-xl border border-border/50 bg-card/30 overflow-hidden">
                    <summary className="cursor-pointer px-4 py-2 bg-secondary/30 text-[10px] uppercase tracking-widest font-bold text-muted-foreground hover:bg-secondary/50">
                        All consolidated counterparties ({trace.consolidated_counterparties.length}) — ranked by frequency
                    </summary>
                    <div className="divide-y divide-border max-h-96 overflow-y-auto">
                        {trace.consolidated_counterparties.slice(0, 100).map((c) => (
                            <ConsolidatedRow key={`${c.chain}:${c.address}`} cp={c} />
                        ))}
                    </div>
                </details>
            )}

            {trace.members_failed > 0 && (
                <p className="text-[10px] text-amber-400 italic flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    {trace.members_failed} wallet{trace.members_failed === 1 ? '' : 's'} failed to fetch (chain-API timeout or rate limit). Re-trace to retry.
                </p>
            )}
        </div>
    );
};

const ConsolidatedRow: React.FC<{ cp: ConsolidatedCounterparty; accent?: 'rose' }> = ({ cp, accent }) => {
    const txs = cp.transactions || [];
    const hasTxs = txs.length > 0;
    const [open, setOpen] = useState(false);
    return (
        <div>
            <button
                type="button"
                onClick={hasTxs ? () => setOpen(v => !v) : undefined}
                disabled={!hasTxs}
                className={`w-full px-4 py-2 hover:bg-secondary/30 flex items-center gap-3 text-xs text-left ${accent === 'rose' ? 'hover:bg-rose-500/10' : ''} ${hasTxs ? 'cursor-pointer' : 'cursor-default'}`}
                title={hasTxs ? 'Click to view transaction breakdown' : 'No transaction breakdown available'}
            >
                {hasTxs
                    ? (open ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" /> : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />)
                    : <span className="w-3.5 h-3.5 flex-shrink-0" aria-hidden />}
                <span className="px-1.5 py-0.5 rounded bg-secondary text-[9px] font-bold uppercase tracking-tight w-12 text-center flex-shrink-0">
                    {cp.chain}
                </span>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        {cp.label && <span className="font-bold text-foreground">{cp.label}</span>}
                        {cp.category && (
                            <span className="px-1.5 py-0.5 rounded bg-primary/10 text-primary border border-primary/20 text-[9px] font-bold uppercase">
                                {cp.category}
                            </span>
                        )}
                        {cp.risk_level && (cp.risk_level === 'critical' || cp.risk_level === 'high') && (
                            <span className="px-1.5 py-0.5 rounded bg-rose-500/10 text-rose-400 border border-rose-500/20 text-[9px] font-bold uppercase">
                                {cp.risk_level}
                            </span>
                        )}
                    </div>
                    <p className="font-mono text-[10px] text-muted-foreground break-all mt-0.5">{cp.address}</p>
                </div>
                <span className="text-[10px] text-muted-foreground tabular-nums w-20 text-right">
                    {cp.seen_in_members} wallet{cp.seen_in_members === 1 ? '' : 's'}
                </span>
                <span className="text-xs font-bold tabular-nums w-12 text-right">{cp.tx_count}</span>
            </button>
            {open && hasTxs && (
                <CounterpartyTxTable txs={txs} accent={accent} />
            )}
        </div>
    );
};

// Drilldown table — same icon style as the Node Inspector Transfers tab.
// Rendered when a high-risk row is expanded, showing tx hash, amount,
// timestamp, direction, and which operator wallet hit this counterparty.
const CounterpartyTxTable: React.FC<{ txs: import('../services/api').CounterpartyTxRef[]; accent?: 'rose' }> = ({ txs, accent }) => (
    <div className={`px-4 py-2 ${accent === 'rose' ? 'bg-rose-500/5' : 'bg-secondary/20'} border-t ${accent === 'rose' ? 'border-rose-500/20' : 'border-border'}`}>
        <table className="w-full text-[10px]">
            <thead>
                <tr className="text-[9px] uppercase tracking-tight text-muted-foreground">
                    <th className="px-2 py-1 text-left w-6"></th>
                    <th className="px-2 py-1 text-left">Tx hash</th>
                    <th className="px-2 py-1 text-left">From operator wallet</th>
                    <th className="px-2 py-1 text-right">Amount</th>
                    <th className="px-2 py-1 text-left">Date</th>
                </tr>
            </thead>
            <tbody className="divide-y divide-border/50">
                {txs.map((t, i) => {
                    const isIn = t.direction === 'incoming';
                    const isOut = t.direction === 'outgoing';
                    return (
                        <tr key={t.tx_hash || i} className="hover:bg-secondary/30">
                            <td className="px-2 py-1.5">
                                {isIn && <ArrowDownLeft className="w-3.5 h-3.5 text-emerald-400" />}
                                {isOut && <ArrowUpRight className="w-3.5 h-3.5 text-rose-400" />}
                                {!isIn && !isOut && <span className="text-muted-foreground">—</span>}
                            </td>
                            <td className="px-2 py-1.5 font-mono text-muted-foreground truncate max-w-[160px]" title={t.tx_hash || undefined}>
                                {t.tx_hash ? `${t.tx_hash.slice(0, 10)}…${t.tx_hash.slice(-6)}` : '—'}
                            </td>
                            <td className="px-2 py-1.5 font-mono text-muted-foreground truncate max-w-[160px]" title={t.member_address || undefined}>
                                {t.member_address ? `${t.member_address.slice(0, 8)}…${t.member_address.slice(-6)}` : '—'}
                            </td>
                            <td className="px-2 py-1.5 text-right tabular-nums">
                                {t.value != null ? `${t.value} ${t.token || ''}`.trim() : '—'}
                            </td>
                            <td className="px-2 py-1.5 text-muted-foreground tabular-nums">
                                {t.timestamp ? formatShortDate(t.timestamp) : '—'}
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    </div>
);

// ── Stage 5: pattern detector + AI narrative ───────────────────────────

const SEVERITY_META: Record<string, { color: string; bg: string; border: string; label: string }> = {
    critical: { color: 'text-rose-300',    bg: 'bg-rose-500/10',    border: 'border-rose-500/30',    label: 'Critical' },
    high:     { color: 'text-amber-300',   bg: 'bg-amber-500/10',   border: 'border-amber-500/30',   label: 'High' },
    medium:   { color: 'text-primary',    bg: 'bg-primary/10',    border: 'border-primary/30',    label: 'Medium' },
    low:      { color: 'text-emerald-300', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', label: 'Low' },
    info:     { color: 'text-muted-foreground', bg: 'bg-muted/20',  border: 'border-border',         label: 'Info' },
};

interface MacroSynthesisSectionProps {
    synthesis: MacroSynthesisResponse | null;
    onRun: () => void;
    synthesizing: boolean;
    hasClusterTrace: boolean;
    canSynthesize: boolean;  // gated on ledger + entities present
}

const MacroSynthesisSection: React.FC<MacroSynthesisSectionProps> = ({
    synthesis, onRun, synthesizing, hasClusterTrace, canSynthesize,
}) => {
    return (
        <section className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <h2 className="text-sm font-black uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                    <Brain className="w-4 h-4" /> Stage 5 — Pattern + AI synthesis
                    <span className="px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20 text-[9px] font-bold uppercase flex items-center gap-1">
                        <Coins className="w-2.5 h-2.5" />Paid
                    </span>
                </h2>
                <button
                    onClick={onRun}
                    disabled={synthesizing || !canSynthesize}
                    className={`flex items-center gap-1.5 px-3 h-9 font-bold text-sm rounded-md disabled:opacity-50 disabled:cursor-not-allowed ${brandGhostBtn}`}
                    title={
                        !canSynthesize
                            ? 'Run Stages 0–2 first (Re-run button at top) so ledger + entities are available'
                            : synthesis ? 'Re-run synthesis' : 'Detect patterns + generate AI executive summary'
                    }
                >
                    {synthesizing
                        ? <><Loader2 className="w-4 h-4 animate-spin" /> Synthesizing…</>
                        : synthesis
                            ? <><RefreshCw className="w-4 h-4" /> Re-synthesize</>
                            : <><Brain className="w-4 h-4" /> Run synthesis</>}
                </button>
            </div>

            {!synthesis && !synthesizing && (
                <div className="rounded-lg border border-dashed border-border bg-card/30 p-6 text-center text-xs text-muted-foreground">
                    Runs the deterministic pattern detector over Stages 0–{hasClusterTrace ? '4' : '2'} and then generates an
                    AI executive summary grounded in those patterns. Patterns are court-defensible; the narrative is a polish layer.
                    {!canSynthesize && (
                        <p className="mt-2 text-amber-400 italic">
                            Click <span className="font-bold">Re-run</span> in the page header to refresh ledger + entities first
                            — this card needs the full Stage 0–2 dataset to synthesize.
                        </p>
                    )}
                    {canSynthesize && !hasClusterTrace && (
                        <p className="mt-2 text-amber-400 italic">
                            Tip: run Stage 4 first to feed cluster-trace findings into the synthesis (post-window activity, off-ramp
                            concentration, on-chain red flags).
                        </p>
                    )}
                </div>
            )}

            {synthesis && <MacroSynthesisResults synthesis={synthesis} />}
        </section>
    );
};

const MacroSynthesisResults: React.FC<{ synthesis: MacroSynthesisResponse }> = ({ synthesis }) => {
    const groupedBySeverity: Record<string, DetectedPattern[]> = {};
    for (const p of synthesis.patterns) {
        (groupedBySeverity[p.severity] = groupedBySeverity[p.severity] || []).push(p);
    }
    const severityOrder = ['critical', 'high', 'medium', 'low', 'info'];

    return (
        <div className="space-y-4">
            {/* Narrative card */}
            <div className="rounded-xl border border-border bg-card p-5 space-y-3">
                <div className="flex items-center justify-between">
                    <div className={`flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold ${brandIconAccent}`}>
                        <FileText className="w-3.5 h-3.5" /> Executive summary
                    </div>
                    <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                        {synthesis.ai_used
                            ? <span className="text-primary">via {synthesis.llm_provider}</span>
                            : <span className="text-amber-400 italic">narrative from patterns (LLM unavailable)</span>}
                        <span>· {synthesis.generated_at?.slice(0, 19).replace('T', ' ')} UTC</span>
                    </div>
                </div>
                <div className="text-sm leading-relaxed whitespace-pre-wrap text-foreground/90">
                    {synthesis.narrative}
                </div>
            </div>

            {/* Pattern signal cards, grouped by severity */}
            {synthesis.patterns.length > 0 ? (
                <div className="space-y-3">
                    <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
                        Detected patterns ({synthesis.patterns.length})
                    </p>
                    {severityOrder.filter(s => groupedBySeverity[s]).map(sev => (
                        <div key={sev} className="space-y-2">
                            {groupedBySeverity[sev].map(p => (
                                <PatternCard key={p.id} pattern={p} />
                            ))}
                        </div>
                    ))}
                </div>
            ) : (
                <p className="text-xs text-muted-foreground italic">No patterns triggered — operator activity falls within baseline behaviour.</p>
            )}
        </div>
    );
};

const PatternCard: React.FC<{ pattern: DetectedPattern }> = ({ pattern }) => {
    const meta = SEVERITY_META[pattern.severity] || SEVERITY_META.info;
    const [open, setOpen] = useState(false);
    const hasEvidence = pattern.evidence && Object.keys(pattern.evidence).length > 0;
    return (
        <div className={`rounded-lg border ${meta.border} ${meta.bg}`}>
            <div className="px-4 py-3 flex items-start gap-3">
                <span className={`px-2 py-0.5 rounded ${meta.bg} ${meta.color} border ${meta.border} text-[9px] font-black uppercase tracking-tight flex-shrink-0 mt-0.5`}>
                    {meta.label}
                </span>
                <div className="flex-1 min-w-0 space-y-1">
                    <p className={`text-sm font-bold ${meta.color}`}>{pattern.title}</p>
                    <p className="text-xs text-muted-foreground">{pattern.summary}</p>
                </div>
                {hasEvidence && (
                    <button
                        onClick={() => setOpen(v => !v)}
                        className="p-1 text-muted-foreground hover:text-foreground transition-all flex-shrink-0"
                        title={open ? 'Hide evidence' : 'Show evidence'}
                    >
                        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                )}
            </div>
            {open && hasEvidence && (
                <pre className="px-4 pb-3 pt-1 text-[10px] text-muted-foreground overflow-x-auto whitespace-pre-wrap font-mono">
                    {JSON.stringify(pattern.evidence, null, 2)}
                </pre>
            )}
        </div>
    );
};

export default CaseMacro;
